import { gql } from '@apollo/client';
import { Button, Divider, Space } from 'antd';
import Page from 'components/Page';
import StatusTag from 'components/StatusTag';
import Table from 'components/Table';
import { renderDateTime } from 'components/Table/renderers';
import { useQuery } from 'hooks/apollo';
import React, { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { LinkState, WebsiteCheckStatus } from 'shared/services/prisma/types';
import Modal from 'components/Modal';
import { numberSorter } from 'components/Table/sorters';

const WEBSITE_CHECK_RESULT = gql`
  query ($id: ID!) {
    websiteCheck(id: $id) {
      id
      createdAt
      entryUrl
      status
      links {
        id
        url
        state
        status
        parent
      }
    }
  }
`;

const brokenLinksColumns = [{
  title: 'Link',
  dataIndex: 'url',
  type: 'url',
}, {
  title: 'Status',
  dataIndex: 'status',
  type: 'number',
}, {
  title: 'Page',
  dataIndex: 'parent',
  type: 'url',
}];

const BrokenLinksResult = () => {
  const { params } = useRouteMatch('/data-sources/:dataSourceId/analytics/:page?/:checkId');
  const { data, loading, startPolling, stopPolling } = useQuery(WEBSITE_CHECK_RESULT, {
    variables: {
      id: params.checkId,
    },
  });

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalTitle, setModalTitle] = useState('');

  useEffect(() => {
    if ([WebsiteCheckStatus.Pending, WebsiteCheckStatus.Running].includes(data?.websiteCheck.status)) {
      startPolling(1000);
    } else {
      stopPolling();
    }
  }, [data?.websiteCheck.status]);

  const brokenLinks = data?.websiteCheck.links.filter(l => l.state === LinkState.Broken);

  const pages = data?.websiteCheck.links.reduce((acc, link) => {
    let page = acc.find(p => p.url === link.parent);
    if (!page) {
      page = {
        id: link.parent,
        url: link.parent,
        okLinks: [],
        skippedLinks: [],
        brokenLinks: [],
      };
      acc.push(page);
    }
    if (link.state === LinkState.Broken) {
      page.brokenLinks.push(link);
    } else if (link.state === LinkState.Skipped) {
      page.skippedLinks.push(link);
    } else {
      page.okLinks.push(link);
    }
    return acc;
  }, []);

  const handleShowModal = (links, title) => {
    setModalData(links);
    setModalTitle(title);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const renderLinks = (links, title) => (
    <Space>
      {links.length}
      <Button onClick={() => handleShowModal(links, title)}>Show</Button>
    </Space>
  );

  const pageColumns = [{
    title: 'Page',
    dataIndex: 'url',
    type: 'url',
  }, {
    title: 'Links',
    dataIndex: 'okLinks',
    type: 'number',
    render: (links) => renderLinks(links, 'Links'),
    sorter: numberSorter('okLinks.length'),
  },
  {
    title: 'Skipped Links',
    dataIndex: 'skippedLinks',
    type: 'number',
    render: (links) => renderLinks(links, 'Skipped links'),
    sorter: numberSorter('skippedLinks.length'),
  }, {
    title: 'Broken links',
    dataIndex: 'brokenLinks',
    type: 'number',
    render: (links) => renderLinks(links, 'Broken links'),
    sorter: numberSorter('brokenLinks.length'),
  }];

  return (
    <Page>
      <Page.Header title="Website check">
        <Space split={<Divider type="vertical" />}>
          <StatusTag status={data?.websiteCheck.status} />
          {renderDateTime(data?.websiteCheck.createdAt)}
          {data?.websiteCheck.entryUrl}
        </Space>

      </Page.Header>
      <Page.Content title="Brokens links">
        <Table dataSource={brokenLinks} columns={brokenLinksColumns} loading={loading} />
      </Page.Content>
      <Page.Content title="Full result">
        <Table
          dataSource={pages}
          columns={pageColumns}
          loading={loading}
        />
      </Page.Content>
      <Modal title={modalTitle} open={showModal} onCancel={handleCloseModal} footer={null} fullWidth={600}>
        <Table
          dataSource={modalData}
          columns={[
            {
              title: 'Link URL',
              dataIndex: 'url',
              key: 'url',
              render: (text) => <a href={text} target="_blank">{text}</a>,
            },
            {
              title: 'State',
              dataIndex: 'state',
              align: 'center',
              defaultSortOrder: 'ascend',
              render: (text) => <StatusTag status={text} />,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
            },
          ]}
        />
      </Modal>
    </Page>
  );
};

export default BrokenLinksResult;
